// Thème Maison : stories façon réseaux sociaux.
// Rien n'est chargé avant le clic : le média de chaque story dort dans un <template>
// et n'est copié dans le lecteur qu'à l'ouverture.
// Tap gauche / droite, glisser vers le bas pour fermer, appui long = pause, son coupé par défaut.

const SWIPE_CLOSE = 90; // px vers le bas pour fermer
const SWIPE_NAV = 50; // px de côté pour changer de story
const HOLD_DELAY = 220; // ms avant de considérer un appui comme « maintenu »

class MxStories extends HTMLElement {
  connectedCallback() {
    this.player = /** @type {HTMLDialogElement|null} */ (this.querySelector('[data-mx-player]'));
    if (!this.player) return;

    this.frame = this.player.querySelector('[data-mx-frame]');
    this.stage = this.player.querySelector('[data-mx-stage]');
    this.bars = this.player.querySelector('[data-mx-bars]');
    this.caption = this.player.querySelector('[data-mx-caption]');
    this.cta = this.player.querySelector('[data-mx-cta]');
    this.muteBtn = this.player.querySelector('[data-mx-mute]');
    this.pauseBtn = this.player.querySelector('[data-mx-pause]');

    this.imageDuration = (Number(this.dataset.duration) || 5) * 1000;
    this.index = -1;
    this.muted = true;
    this.paused = false;
    this.userPaused = false;
    this.elapsed = 0;
    this.lastTick = 0;
    this.raf = 0;
    this.suppressClick = false;

    this.onRailClick = this.onRailClick.bind(this);
    this.onPlayerClick = this.onPlayerClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
    this.tick = this.tick.bind(this);

    this.addEventListener('click', this.onRailClick);
    this.player.addEventListener('click', this.onPlayerClick);
    this.player.addEventListener('keydown', this.onKeydown);
    this.player.addEventListener('close', this.onClose);
    this.frame.addEventListener('pointerdown', this.onPointerDown);
    this.frame.addEventListener('pointermove', this.onPointerMove);
    this.frame.addEventListener('pointerup', this.onPointerUp);
    this.frame.addEventListener('pointercancel', this.onPointerUp);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.player?.open) this.player.close();
  }

  /** Stories de ce composant seulement. */
  get stories() {
    return [...this.querySelectorAll('[data-mx-story]')].filter((el) => el.closest('mx-stories') === this);
  }

  onRailClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const opener = target?.closest('[data-mx-open]');
    if (!opener || this.player.contains(opener)) return;
    const story = opener.closest('[data-mx-story]');
    const index = this.stories.indexOf(story);
    if (index >= 0) this.open(index);
  }

  open(index) {
    this.buildBars();
    if (!this.player.open) {
      this.player.showModal();
      document.documentElement.style.overflow = 'hidden';
    }
    this.show(index);
  }

  buildBars() {
    const count = this.stories.length;
    this.bars.replaceChildren();
    for (let i = 0; i < count; i++) {
      const bar = document.createElement('span');
      bar.className = 'mx-stories__bar';
      const fill = document.createElement('span');
      fill.className = 'mx-stories__bar-fill';
      bar.append(fill);
      this.bars.append(bar);
    }
  }

  show(index) {
    const stories = this.stories;
    if (index < 0) index = 0;
    if (index >= stories.length) {
      this.player.close();
      return;
    }

    this.stop();
    this.index = index;
    this.elapsed = 0;
    this.userPaused = false;
    this.paused = false;
    this.pauseBtn?.setAttribute('aria-pressed', 'false');

    const story = stories[index];
    story.classList.add('is-seen');

    // Média : copié depuis le <template> à ce moment seulement.
    const tpl = /** @type {HTMLTemplateElement|null} */ (story.querySelector('template[data-mx-media]'));
    this.stage.replaceChildren(tpl ? tpl.content.cloneNode(true) : '');
    const ctaTpl = /** @type {HTMLTemplateElement|null} */ (story.querySelector('template[data-mx-cta]'));
    this.cta.replaceChildren(ctaTpl ? ctaTpl.content.cloneNode(true) : '');
    this.caption.textContent = story.querySelector('[data-mx-label]')?.textContent?.trim() ?? '';

    this.updateBars(0);

    this.video = this.stage.querySelector('video');
    if (this.video) {
      const video = this.video;
      video.muted = this.muted;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.removeAttribute('controls');
      video.loop = false;
      this.storyAbort = new AbortController();
      const { signal } = this.storyAbort;
      video.addEventListener('ended', () => this.next(), { once: true, signal });
      video.addEventListener('error', () => this.next(), { once: true, signal });
      this.muteBtn.hidden = false;
      this.syncMute();
      video.play().catch(() => {
        // Lecture refusée par le navigateur : on coupe le son et on réessaie.
        video.muted = true;
        this.muted = true;
        this.syncMute();
        video.play().catch(() => {});
      });
    } else {
      this.muteBtn.hidden = true;
    }

    this.lastTick = performance.now();
    this.raf = requestAnimationFrame(this.tick);
  }

  tick(now) {
    const delta = now - this.lastTick;
    this.lastTick = now;

    let progress = 0;
    if (this.video) {
      const { currentTime, duration } = this.video;
      progress = duration && Number.isFinite(duration) ? currentTime / duration : 0;
    } else {
      if (!this.paused) this.elapsed += delta;
      progress = this.elapsed / this.imageDuration;
      if (progress >= 1) {
        this.next();
        return;
      }
    }
    this.updateBars(Math.min(progress, 1));
    this.raf = requestAnimationFrame(this.tick);
  }

  updateBars(progress) {
    const fills = this.bars.querySelectorAll('.mx-stories__bar-fill');
    fills.forEach((fill, i) => {
      const value = i < this.index ? 1 : i === this.index ? progress : 0;
      /** @type {HTMLElement} */ (fill).style.transform = `scaleX(${value})`;
    });
  }

  next() {
    this.show(this.index + 1);
  }

  prev() {
    if (this.index <= 0) {
      this.show(0);
    } else {
      this.show(this.index - 1);
    }
  }

  setPaused(paused) {
    this.paused = paused;
    if (this.video) {
      if (paused) this.video.pause();
      else this.video.play().catch(() => {});
    }
  }

  togglePause() {
    this.userPaused = !this.userPaused;
    this.pauseBtn?.setAttribute('aria-pressed', String(this.userPaused));
    this.setPaused(this.userPaused);
  }

  toggleMute() {
    this.muted = !this.muted;
    if (this.video) this.video.muted = this.muted;
    this.syncMute();
  }

  syncMute() {
    this.muteBtn?.setAttribute('aria-pressed', String(this.muted));
  }

  stop() {
    cancelAnimationFrame(this.raf);
    // Coupe les écouteurs de la story précédente avant de vider la vidéo.
    this.storyAbort?.abort();
    this.storyAbort = null;
    if (this.video) {
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.querySelectorAll('source').forEach((s) => s.remove());
      this.video.load();
      this.video = null;
    }
  }

  onClose() {
    this.stop();
    this.stage.replaceChildren();
    this.cta.replaceChildren();
    this.index = -1;
    this.frame.style.transform = '';
    this.frame.style.opacity = '';
    document.documentElement.style.overflow = '';
  }

  onVisibility() {
    if (!this.player.open) return;
    if (document.hidden) this.setPaused(true);
    else if (!this.userPaused) this.setPaused(false);
  }

  onPlayerClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Clic sur le fond sombre autour du cadre (ordinateur) : fermer.
    if (target === this.player) {
      this.player.close();
      return;
    }
    if (this.suppressClick) {
      this.suppressClick = false;
      if (target.closest('[data-mx-prev], [data-mx-next]')) {
        event.preventDefault();
        return;
      }
    }
    if (target.closest('[data-mx-close]')) this.player.close();
    else if (target.closest('[data-mx-mute]')) this.toggleMute();
    else if (target.closest('[data-mx-pause]')) this.togglePause();
    else if (target.closest('[data-mx-prev]')) this.prev();
    else if (target.closest('[data-mx-next]')) this.next();
  }

  onKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  }

  /* --- Gestes : appui long = pause, glisser bas = fermer, glisser côté = changer --- */

  onPointerDown(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest('a, [data-mx-close], [data-mx-mute], [data-mx-pause]')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.gesture = { x: event.clientX, y: event.clientY, held: false, id: event.pointerId };
    this.suppressClick = false;
    clearTimeout(this.holdTimer);
    this.holdTimer = setTimeout(() => {
      if (!this.gesture) return;
      this.gesture.held = true;
      if (!this.userPaused) this.setPaused(true);
    }, HOLD_DELAY);
  }

  onPointerMove(event) {
    if (!this.gesture || event.pointerId !== this.gesture.id) return;
    const dy = event.clientY - this.gesture.y;
    const dx = event.clientX - this.gesture.x;
    if (dy > 10 && dy > Math.abs(dx)) {
      this.frame.classList.add('is-dragging');
      this.frame.style.transform = `translateY(${dy}px)`;
      this.frame.style.opacity = String(Math.max(0.4, 1 - dy / 600));
    }
  }

  onPointerUp(event) {
    clearTimeout(this.holdTimer);
    const gesture = this.gesture;
    this.gesture = null;
    if (!gesture || event.pointerId !== gesture.id) return;

    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    this.frame.classList.remove('is-dragging');

    if (dy > SWIPE_CLOSE && dy > Math.abs(dx)) {
      this.suppressClick = true;
      this.player.close();
      return;
    }

    this.frame.style.transform = '';
    this.frame.style.opacity = '';

    if (Math.abs(dx) > SWIPE_NAV && Math.abs(dx) > Math.abs(dy)) {
      this.suppressClick = true;
      if (dx < 0) this.next();
      else this.prev();
      return;
    }

    if (gesture.held) {
      // Fin de l'appui long : on reprend (sauf pause demandée par le bouton).
      this.suppressClick = true;
      if (!this.userPaused) this.setPaused(false);
    }
  }
}

if (!customElements.get('mx-stories')) customElements.define('mx-stories', MxStories);
